import React, { useState, useEffect, useMemo } from 'react'
import { Reply, TrendingUp, User, MessageSquare, Eye, ExternalLink, ArrowLeft, Download, Filter, X, Edit2, Save, ChevronLeft, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { utcStartOfDay, utcEndOfDay, utcStartOfMonth, utcStartOfPrevMonth, utcEndOfPrevMonth } from '@/lib/utc-date-utils'
import { useConversionFunnel } from '@/hooks/useConversionFunnel'
import { supabase } from '@/integrations/supabase/client'
import { resolveClient } from '@/lib/resolve-client-filter'
import { toast } from "@/hooks/use-toast"
import { TimeframeSelector } from './TimeframeSelector'
import { ReplyDetailModal } from './ReplyDetailModal'
import { DataTableColumnConfig, ColumnConfig } from './DataTableColumnConfig'
import Papa from 'papaparse'
import { useQuery, useQueryClient } from '@tanstack/react-query'

interface PositiveRepliesDetailViewProps {
  onBack: () => void
  clientFilter?: string
  timeFrame?: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange?: (timeFrame: string) => void
  onCustomDateRangeChange?: (dateRange: { from: Date; to: Date } | undefined) => void
  initialCategoryFilter?: string
}

export const PositiveRepliesDetailView = ({ 
  onBack, 
  clientFilter = 'all', 
  timeFrame = 'MTD', 
  customDateRange,
  onTimeFrameChange,
  onCustomDateRangeChange,
  initialCategoryFilter = 'all'
}: PositiveRepliesDetailViewProps) => {
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [selectedReply, setSelectedReply] = useState<any>(null)
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // State for reply detail modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // Editing state for category
  const [editingCategory, setEditingCategory] = useState(false)
  const [editedCategory, setEditedCategory] = useState('')
  const [savingCategory, setSavingCategory] = useState(false)
  
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<string>(initialCategoryFilter)
  const [clientIdFilter, setClientIdFilter] = useState<string>('')
  const [fromEmailFilter, setFromEmailFilter] = useState<string>('')
  const [toEmailFilter, setToEmailFilter] = useState<string>('')
  const [campaignFilter, setCampaignFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  // Available category options for dropdown
  const categoryOptions = [
    'Interested',
    'Information Request', 
    'Meeting Request',
    'Meeting Booked',
    'Specific Questions',
    'Out of Office',
    'Auto-Reply',
    'Automated Response',
    'No Longer Works Here',
    'Customer Service',
    'Not Interested',
    'Unsubscribe Request',
    'Spam/Junk',
    'Complaint',
    'Negative',
    'Rude',
    'Hostile',
    'Do Not Contact',
    'Neutral',
    'Wrong Person',
    'Reach Out Later',
    'OOO',
    'Vacation',
    'Holiday',
    'Maternity',
    'Paternity',
    'Leave',
    'Traveling',
    'Away',
    'Unavailable'
  ]
  
  // Column configuration state
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>([
    { id: 'lead', label: 'Lead', visible: true, order: 0 },
    { id: 'sender', label: 'Sender', visible: true, order: 1 },
    { id: 'campaign', label: 'Campaign', visible: true, order: 2 },
    { id: 'category', label: 'Category', visible: true, order: 3 },
    { id: 'reply_count', label: 'Reply Count', visible: true, order: 4 },
    { id: 'reply', label: 'Reply', visible: true, order: 5 },
    { id: 'actions', label: 'Actions', visible: true, order: 6 },
    { id: 'time_replied', label: 'Time Replied', visible: false, order: 7 },
    { id: 'from_email', label: 'From Email', visible: false, order: 8 },
    { id: 'to_email', label: 'To Email', visible: false, order: 9 },
    { id: 'to_name', label: 'Contact', visible: false, order: 10 },
    { id: 'campaign_name', label: 'Campaign Name', visible: false, order: 11 },
    { id: 'campaign_id', label: 'Campaign ID', visible: false, order: 12 },
    { id: 'response_category', label: 'Response Category', visible: false, order: 13 },
    { id: 'reply_type', label: 'Reply Type', visible: false, order: 14 },
    { id: 'client_id', label: 'Client ID', visible: false, order: 15 },
    { id: 'reply_body', label: 'Reply Content', visible: false, order: 16 },
    { id: 'subject', label: 'Subject', visible: false, order: 17 },
    { id: 'sent_message', label: 'Original Message', visible: false, order: 18 },
    { id: 'app_url', label: 'Smartlead URL', visible: false, order: 19 },
  ])
  
  const queryClient = useQueryClient()
  const { data: conversionData } = useConversionFunnel({ 
    timeFrame,
    clientId: clientFilter,
    dateRange: customDateRange
  })

  // State for positive replies count from funnel (authoritative source)
  const [positiveRepliesFromFunnel, setPositiveRepliesFromFunnel] = useState<number>(0)

  // Update positive replies count when conversion data changes
  useEffect(() => {
    if (conversionData?.positiveReplies !== undefined) {
      setPositiveRepliesFromFunnel(conversionData.positiveReplies)
    }
  }, [conversionData])

  // Get the same date range that conversion funnel uses
  // All boundaries use UTC to match Postgres timestamps regardless of browser timezone.
  const getDateRange = () => {
    const now = new Date()

    if (customDateRange?.from && customDateRange?.to) {
      return {
        startDate: utcStartOfDay(customDateRange.from),
        endDate: utcEndOfDay(customDateRange.to)
      }
    }

    let startDate = new Date()
    let endDate = now

    switch (timeFrame) {
      case 'today':
        startDate = utcStartOfDay(now)
        endDate = utcEndOfDay(now)
        break
      case 'LAST_MONTH':
        startDate = utcStartOfPrevMonth(now)
        endDate = utcEndOfPrevMonth(now)
        break
      case 'MTD':
        startDate = utcStartOfMonth(now)
        endDate = now
        break
      case 'last7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'last90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
        break
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    }

    return { startDate, endDate }
  }

  // Cached query for positive replies data - FILTERED FOR POSITIVE REPLIES ONLY
  const { data: positiveRepliesData = [], isLoading } = useQuery({
    queryKey: ['positiveReplies', clientFilter, timeFrame, customDateRange],
    queryFn: async () => {
      const { startDate: correctStartDate, endDate: correctEndDate } = getDateRange()
      const { workspaceName } = await resolveClient(clientFilter)

      let query = supabase
        .from('masterinbox_replies')
        .select(`
          id,
          created_at,
          to_address,
          email,
          to_name,
          campaign_name,
          campaign_id,
          label_name,
          reply_category_type,
          workspace_name,
          reply_count,
          text,
          reply_message,
          subject
        `)
        .eq('reply_count', 1)
        .eq('reply_category_type', 'positive')  // Only positive replies

      // Client filtering
      if (workspaceName) {
        query = query.eq('workspace_name', workspaceName)
      }

      // Date filtering
      query = query
        .gte('created_at', correctStartDate.toISOString())
        .lte('created_at', correctEndDate.toISOString())

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        console.error('Error fetching positive replies:', error)
        return []
      }

      console.log('Positive replies data:', data?.length || 0, 'records')

      // Process data
      const processedData = (data || []).map(reply => {
        const computedType = reply.reply_category_type || 'positive'

        return {
          ...reply,
          reply_type_computed: computedType
        }
      })

      return processedData
    },
    enabled: true,
    staleTime: 10 * 60 * 1000, // Data is fresh for 10 minutes (increased from 5)
    refetchInterval: false, // Disable auto-refetch to improve performance
    refetchOnWindowFocus: false, // Disable refetch on focus to improve performance
    retry: 1, // Reduce retries to 1
    gcTime: 15 * 60 * 1000 // Keep in cache for 15 minutes
  })

  // Filter the data based on current filters
  const filteredReplies = useMemo(() => {
    let filtered = positiveRepliesData || []

    // Apply category filter
    if (categoryFilter && categoryFilter !== 'all') {
      filtered = filtered.filter(reply =>
        reply.label_name === categoryFilter
      )
    }

    // Apply other filters
    if (clientIdFilter) {
      filtered = filtered.filter(reply =>
        reply.workspace_name?.toString().includes(clientIdFilter)
      )
    }

    if (fromEmailFilter) {
      filtered = filtered.filter(reply =>
        reply.to_address?.toLowerCase().includes(fromEmailFilter.toLowerCase())
      )
    }

    if (toEmailFilter) {
      filtered = filtered.filter(reply =>
        reply.email?.toLowerCase().includes(toEmailFilter.toLowerCase())
      )
    }

    if (campaignFilter) {
      filtered = filtered.filter(reply =>
        reply.campaign_name?.toLowerCase().includes(campaignFilter.toLowerCase())
      )
    }

    return filtered
  }, [positiveRepliesData, categoryFilter, clientIdFilter, fromEmailFilter, toEmailFilter, campaignFilter])

  // Calculate average per day
  const calculateAvgPerDay = () => {
    const { startDate, endDate } = getDateRange()
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    return totalDays > 0 ? Math.round(filteredReplies.length / totalDays) : 0
  }

  // Get category breakdown for charts - using label_name column
  const getCategoryBreakdown = useMemo(() => {
    const categoryCounts = filteredReplies.reduce((acc, reply) => {
      const category = reply.label_name || 'Unknown'
      acc[category] = (acc[category] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const totalReplies = filteredReplies?.length || 0
    return Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
      percentage: totalReplies > 0 ? ((Number(count) / Number(totalReplies)) * 100).toFixed(1) : '0'
    }))
  }, [filteredReplies])
  
  // Pagination logic
  const totalPages = Math.ceil(filteredReplies.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReplies = filteredReplies.slice(startIndex, endIndex)
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [clientIdFilter, fromEmailFilter, toEmailFilter, campaignFilter])
  
  // Handlers
  const handleRowClick = (reply: any) => {
    setSelectedReply(reply)
    setIsModalOpen(true)
  }
  
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedReply(null)
  }
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Get simplified category breakdown - all positive since filtered
  const getSimplifiedCategoryBreakdown = useMemo(() => {
    return [
      { name: 'Positive', value: positiveRepliesData.length, fill: '#22c55e' }
    ]
  }, [positiveRepliesData])

  // Helper function for manual categorization (fallback from label_name)
  const categorizeReply = (label: string | null | undefined): string => {
    if (!label) return 'ai_messup';

    const cat = label.toLowerCase().trim();

    if (['pricing inquiry', 'specific questions', 'meeting request', 'meeting booked', 'interested', 'information request'].includes(cat)) {
      return 'positive';
    }
    if (['out of office', 'auto-reply', 'sender originated bounce', 'no longer works here', 'reach out later'].includes(cat)) {
      return 'ooo_bounced';
    }
    if (['do not contact', 'not interested', 'campaign mess up'].includes(cat)) {
      return 'negative';
    }
    if (['wrong person'].includes(cat)) {
      return 'ai_messup';
    }
    return 'ai_messup';
  }

  // Helper function to format email content like a normal email
  const formatEmailContent = (content: string) => {
    if (!content) return 'No content available'
    
    // Remove excessive whitespace and format
    return content
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 200) + (content.length > 200 ? '...' : '')
  }

  // Handle category update
  const handleCategoryUpdate = async (replyId: string, newCategory: string) => {
    setSavingCategory(true)
    try {
      const newReplyType = categorizeReply(newCategory)
      const { error } = await supabase
        .from('masterinbox_replies')
        .update({ label_name: newCategory, reply_category_type: newReplyType })
        .eq('id', replyId)

      if (error) {
        console.error('Error updating category:', error)
        toast({
          title: "Error",
          description: "Failed to update category",
          variant: "destructive"
        })
        return
      }

      // Invalidate and refetch the data
      await queryClient.invalidateQueries({ queryKey: ['positiveReplies'] })
      
      toast({
        title: "Success",
        description: "Category updated successfully"
      })
      
      setEditingCategory(false)
      setEditedCategory('')
    } catch (error) {
      console.error('Error updating category:', error)
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive"
      })
    } finally {
      setSavingCategory(false)
    }
  }

  // Handle export
  const handleExport = async () => {
    try {
      const exportData = filteredReplies.map(reply => ({
        'Time Replied': reply.created_at,
        'Sender Email': reply.to_address,
        'Lead Email': reply.email,
        'Contact': reply.to_name,
        'Campaign': reply.campaign_name,
        'Category': reply.label_name,
        'Reply Type': reply.reply_category_type,
        'Workspace': reply.workspace_name,
        'Reply Count': reply.reply_count,
        'Reply Content': reply.text
      }))

      const csv = Papa.unparse(exportData)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `positive-replies-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Export error:', error)
      toast({
        title: "Export Error",
        description: "Failed to export data",
        variant: "destructive"
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Timeframe Selector */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
      {onTimeFrameChange && onCustomDateRangeChange && (
        <TimeframeSelector
          timeFrame={timeFrame}
          customDateRange={customDateRange}
          onTimeFrameChange={onTimeFrameChange}
          onCustomDateRangeChange={onCustomDateRangeChange}
        />
      )}
    </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive Replies</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{positiveRepliesFromFunnel || filteredReplies.length}</div>
            <p className="text-xs text-muted-foreground">
              Only interested, meeting requests, and qualified leads
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Day</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{calculateAvgPerDay()}</div>
            <p className="text-xs text-muted-foreground">Positive replies per day in timeframe</p>
          </CardContent>
        </Card>
      </div>

    {/* Category Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <Card>
        <CardHeader>
          <CardTitle>Category Breakdown</CardTitle>
          <CardDescription>Distribution of replies by category</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getCategoryBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="category" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip 
                formatter={(value: number, name: string) => [
                  `${value} replies (${getCategoryBreakdown.find(item => item.count === value)?.percentage || 0}%)`, 
                  'Count'
                ]}
              />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reply Types</CardTitle>
            <CardDescription>Positive, negative, and auto-reply breakdown</CardDescription>
        </CardHeader>
        <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={getSimplifiedCategoryBreakdown}
                onClick={(data) => {
                  if (data && data.activeLabel) {
                    const clickedCategory = data.activeLabel.toLowerCase();
                    const targetCategory = clickedCategory === 'auto-reply' ? 'auto-reply' : clickedCategory;
                    // Set filter to clicked category (don't toggle off if already selected)
                    setCategoryFilter(targetCategory);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value: number) => [`${value} replies`, 'Count']} />
                <Bar dataKey="value" style={{ cursor: 'pointer' }}>
                  {getSimplifiedCategoryBreakdown.map((entry, index) => {
                    const isSelected = categoryFilter !== 'all' && entry.name.toLowerCase() === categoryFilter;
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={isSelected ? entry.fill : entry.fill} 
                        fillOpacity={isSelected ? 1.0 : 0.7}
                        stroke={isSelected ? '#000' : 'none'}
                        strokeWidth={isSelected ? 2 : 0}
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>

    {/* Positive Replies Data Table */}
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-semibold">Positive Replies Data</CardTitle>
            <CardDescription>
                From masterinbox_replies - {filteredReplies.length} of {positiveRepliesData.length} records shown
            </CardDescription>
          </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <DataTableColumnConfig
                columns={columnConfigs}
                onColumnToggle={(columnId) => {
                  setColumnConfigs(prev => prev.map(col => 
                    col.id === columnId ? { ...col, visible: !col.visible } : col
                  ))
                }}
                onColumnReorder={(dragIndex, hoverIndex) => {
                  setColumnConfigs(prev => {
                    const newColumns = [...prev]
                    const draggedItem = newColumns[dragIndex]
                    newColumns.splice(dragIndex, 1)
                    newColumns.splice(hoverIndex, 0, draggedItem)
                    return newColumns.map((col, index) => ({ ...col, order: index }))
                  })
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
        </div>
      </CardHeader>
      <CardContent>
          {/* Filters */}
          {showFilters && (
            <div className="mb-4 p-4 border rounded-lg bg-muted/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categoryOptions.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Client ID</label>
                  <Input
                    placeholder="Filter by Client ID"
                    value={clientIdFilter}
                    onChange={(e) => setClientIdFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">From Email</label>
                  <Input
                    placeholder="Filter by From Email"
                    value={fromEmailFilter}
                    onChange={(e) => setFromEmailFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">To Email</label>
                  <Input
                    placeholder="Filter by To Email"
                    value={toEmailFilter}
                    onChange={(e) => setToEmailFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Campaign</label>
                  <Input
                    placeholder="Filter by Campaign"
                    value={campaignFilter}
                    onChange={(e) => setCampaignFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCategoryFilter('all')
                    setClientIdFilter('')
                    setFromEmailFilter('')
                    setToEmailFilter('')
                    setCampaignFilter('')
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">Loading positive replies...</div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto max-h-[600px] border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columnConfigs
                        .filter(config => config.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(config => (
                          <TableHead key={config.id}>{config.label}</TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedReplies.map((reply, index) => (
                      <TableRow 
                        key={index} 
                        className="hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(reply)}
                      >
                        {columnConfigs
                          .filter(config => config.visible)
                          .sort((a, b) => a.order - b.order)
                          .map(config => (
                            <TableCell key={config.id}>
                              {config.id === 'lead' && (
                                <div className="text-xs">
                                  <div className="font-mono">{reply.email || 'N/A'}</div>
                                  <div className="text-muted-foreground">{reply.to_name || 'Unknown'}</div>
                                </div>
                              )}
                              {config.id === 'sender' && (
                                <div className="text-xs">
                                  <div className="font-mono">{reply.to_address || 'N/A'}</div>
                                </div>
                              )}
                              {config.id === 'campaign' && (
                                <div className="text-xs">
                                  <div title={reply.campaign_name}>{reply.campaign_name || 'N/A'}</div>
                                  <div className="text-muted-foreground font-mono">
                                    {reply.campaign_id || (reply.data as any)?.campaign_id || 'N/A'}
                                  </div>
                                </div>
                              )}
                              {config.id === 'category' && (
                                <div className="text-xs">
                                  <Badge variant="secondary" className="text-xs">
                                    {reply.label_name || 'Unknown'}
                                  </Badge>
                                  <div className="text-muted-foreground mt-1">
                                    <Badge 
                                      variant={reply.reply_category_type === 'positive' ? 'default' : 
                                              reply.reply_category_type === 'negative' ? 'destructive' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {reply.reply_category_type || 'positive'}
                                    </Badge>
                                  </div>
                                </div>
                              )}
                              {config.id === 'reply_count' && (
                                <div className="text-xs">
                                  <div>{reply.reply_count || 'N/A'}</div>
                                  <div className="text-muted-foreground font-mono">
                                    {reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              )}
                              {config.id === 'reply' && (
                                <div className="text-xs max-w-48 truncate" title={reply.text || reply.reply_message}>
                                  {formatEmailContent(reply.text || reply.reply_message)}
                                </div>
                              )}
                              {config.id === 'actions' && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setEditingCategory(true)
                                      setEditedCategory(reply.label_name || '')
                                      setSelectedRecord(reply)
                                    }}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                              {/* Legacy columns for backward compatibility */}
                              {config.id === 'time_replied' && (
                                <span className="text-sm">
                                  {reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'}
                                </span>
                              )}
                              {config.id === 'from_email' && (
                                <span className="text-sm font-mono">{reply.to_address || 'N/A'}</span>
                              )}
                              {config.id === 'to_email' && (
                                <span className="text-sm font-mono">{reply.email || 'N/A'}</span>
                              )}
                              {config.id === 'to_name' && (
                                <span className="text-sm">{reply.to_name || 'N/A'}</span>
                              )}
                              {config.id === 'campaign_name' && (
                                <span className="text-sm">{reply.campaign_name || 'N/A'}</span>
                              )}
                              {config.id === 'response_category' && (
                                <Badge variant="secondary" className="text-xs">
                                  {reply.label_name || 'Unknown'}
                                </Badge>
                              )}
                              {config.id === 'reply_type' && (
                                <Badge 
                                  variant={reply.reply_category_type === 'positive' ? 'default' : 
                                          reply.reply_category_type === 'negative' ? 'destructive' : 'secondary'}
                                  className="text-xs"
                                >
                                  {reply.reply_category_type || 'positive'}
                                </Badge>
                              )}
                              {config.id === 'client_id' && (
                                <span className="text-sm font-mono">{reply.workspace_name || 'N/A'}</span>
                              )}
                              {config.id === 'reply_body' && (
                                <span className="text-sm text-muted-foreground">
                                  {formatEmailContent(reply.text || reply.reply_message)}
                                </span>
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
        </div>
          )}
      </CardContent>
    </Card>

      {/* Category Edit Dialog */}
      <Dialog open={editingCategory} onOpenChange={setEditingCategory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <Select value={editedCategory} onValueChange={setEditedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingCategory(false)
                  setEditedCategory('')
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => selectedRecord && handleCategoryUpdate(selectedRecord.id, editedCategory)}
                disabled={savingCategory}
              >
                {savingCategory ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reply Detail Dialog */}
      <Dialog open={!!selectedReply} onOpenChange={() => setSelectedReply(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reply Details</DialogTitle>
          </DialogHeader>
          {selectedReply && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Sender Email</label>
                  <p className="text-sm">{selectedReply.to_address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Lead Email</label>
                  <p className="text-sm">{selectedReply.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Contact</label>
                  <p className="text-sm">{selectedReply.to_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Campaign</label>
                  <p className="text-sm">{selectedReply.campaign_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <Badge variant="secondary">{selectedReply.label_name}</Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Reply Type</label>
                  <Badge 
                    variant={selectedReply.reply_category_type === 'positive' ? 'default' : 
                            selectedReply.reply_category_type === 'negative' ? 'destructive' : 'secondary'}
                  >
                    {selectedReply.reply_category_type}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Reply Content</label>
                <div className="mt-2 p-4 bg-muted rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedReply.text || selectedReply.reply_message}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
  </div>
)
}